// src/components/ComboBuilderModal.js — v2
// Lets customer pick one item per slot (+ size, if that item is a pizza) at a fixed combo price

import React, { useState } from 'react';
import { optionLabel } from '../utils/addons';
import { SIZES } from '../data/menuData';

const PIZZA_SIZES = SIZES.pizza; // [{label:'Personal',mult:1}, {label:'Share',...}, ...]

export default function ComboBuilderModal({ combo, menuItemsById, addonGroups = [], onClose, onAdd }) {
  const [selections, setSelections] = useState({}); // { slotId: { itemId, sizeLabel, addons: {groupId: optId|optId[]} } }

  const slots = combo.slots || [];

  const isPizza = (itemId) => menuItemsById[itemId]?.sizes === 'pizza';
  const hasAddons = (itemId) => menuItemsById[itemId]?.hasAddon !== false;

  const allFilled = slots.every(s => {
    const sel = selections[s.id];
    if (!sel?.itemId) return false;
    if (isPizza(sel.itemId) && !sel.sizeLabel) return false; // pizza slots need a size chosen too
    return true;
  });

  const pickItem = (slotId, itemId) => setSelections(p => ({
    ...p,
    [slotId]: { itemId, sizeLabel: isPizza(itemId) ? (p[slotId]?.itemId === itemId ? p[slotId]?.sizeLabel : null) : null, addons: p[slotId]?.itemId === itemId ? (p[slotId]?.addons||{}) : {} },
  }));

  const pickSize = (slotId, sizeLabel) => setSelections(p => ({
    ...p, [slotId]: { ...p[slotId], sizeLabel },
  }));

  const toggleAddon = (slotId, gid, oid, stype) => setSelections(p => {
    const s = p[slotId]; if (!s) return p;
    const cur = s.addons?.[gid];
    const next = stype==='single' ? (cur===oid?null:oid) : (cur||[]).includes(oid)?(cur||[]).filter(x=>x!==oid):[...(cur||[]),oid];
    return { ...p, [slotId]: { ...s, addons: { ...s.addons, [gid]: next } } };
  });

  const addonPrice = (opt, sizeLabel) => sizeLabel==='Family' ? (opt.familyPrice||0) : sizeLabel==='Share' ? (opt.sharePrice||0) : (opt.personalPrice||0);
  const addonAmt = (addons, sizeLabel) => { let t=0; addonGroups.forEach(g=>{ const sel=addons?.[g.id]; if(!sel) return; const ids=g.selectionType==='single'?(sel?[sel]:[]):(sel||[]); ids.forEach(oid=>{const o=(g.options||[]).find(x=>x.id===oid); if(o) t+=addonPrice(o,sizeLabel);}); }); return t; };
  const addonNames = (addons) => { const n=[]; addonGroups.forEach(g=>{ const sel=addons?.[g.id]; if(!sel) return; const ids=g.selectionType==='single'?(sel?[sel]:[]):(sel||[]); ids.forEach(oid=>{const o=(g.options||[]).find(x=>x.id===oid); if(o) n.push(optionLabel(o));}); }); return n; };
  const extraTotal = Object.values(selections).reduce((sum,s)=>sum+addonAmt(s.addons,s.sizeLabel),0);

  const handleAdd = () => {
    if (!allFilled) return;
    const chosenLabels = slots.map(s => {
      const sel = selections[s.id];
      const item = menuItemsById[sel.itemId];
      if (!item) return '';
      const names = addonNames(sel.addons);
      const sizePart = sel.sizeLabel ? ` (${sel.sizeLabel})` : '';
      const addonPart = names.length ? ` +${names.join(', ')}` : '';
      return `${item.name}${sizePart}${addonPart}`;
    }).filter(Boolean);

    onAdd({
      menuId: combo.id,
      name: `${combo.name}: ${chosenLabels.join(' + ')}`,
      price: combo.price + extraTotal,
      qty: 1,
      addons: [],
      isCombo: true,
    });
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 500,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start',
      paddingTop: 70, paddingLeft: 210,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 18, width: 440,
        maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,.18)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>🍱 {combo.name}</div>
        <div style={{ fontSize: 13, color: '#D85A30', fontWeight: 600, marginBottom: 14 }}>Fixed price: ₹{combo.price}</div>

        {slots.map(slot => {
          const sel = selections[slot.id] || {};
          const chosenIsPizza = sel.itemId && isPizza(sel.itemId);
          return (
            <div key={slot.id} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 7 }}>
                {slot.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: chosenIsPizza ? 8 : 0 }}>
                {(slot.itemIds || []).map(itemId => {
                  const item = menuItemsById[itemId];
                  if (!item) return null;
                  const selected = sel.itemId === itemId;
                  return (
                    <div key={itemId} onClick={() => pickItem(slot.id, itemId)} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 9px', borderRadius: 8,
                      cursor: 'pointer', fontSize: 12,
                      border: `1.5px solid ${selected ? '#D85A30' : '#eee'}`,
                      background: selected ? '#FAECE7' : '#fff',
                    }}>
                      <span>{item.emoji}</span>
                      <span style={{ fontWeight: selected ? 500 : 400 }}>{item.name}</span>
                    </div>
                  );
                })}
              </div>

              {/* Size picker — only shown when the chosen item is a pizza. Respects admin's allowed sizes. */}
              {chosenIsPizza && (() => {
                const allowedSizes = (slot.allowedSizes && slot.allowedSizes.length) ? slot.allowedSizes : PIZZA_SIZES.map(s=>s.label);
                const visibleSizes = PIZZA_SIZES.filter(s => allowedSizes.includes(s.label));
                return (
                  <div style={{ padding: '8px 10px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6 }}>Choose size</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {visibleSizes.map(s => {
                        const active = sel.sizeLabel === s.label;
                        return (
                          <div key={s.label} onClick={() => pickSize(slot.id, s.label)} style={{
                            padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                            border: `1px solid ${active ? '#D85A30' : '#eee'}`,
                            background: active ? '#FAECE7' : '#fff', color: active ? '#993C1D' : '#555',
                          }}>{s.label}</div>
                        );
                      })}
                    </div>

                    {/* Toppings — opens once a size is picked, matching regular pizza items */}
                    {sel.sizeLabel && hasAddons(sel.itemId) && addonGroups.filter(g=>(g.appliesTo||['pizza']).includes('pizza')).map(g => {
                      const opts = (g.options||[]).filter(o=>o.active!==false); if (!opts.length) return null;
                      return (
                        <div key={g.id} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 5 }}>{g.name} <span style={{ fontWeight: 400 }}>({g.selectionType==='single'?'Choose one':'Choose many'})</span></div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {opts.map(opt => {
                              const pr = addonPrice(opt, sel.sizeLabel);
                              const selVal = sel.addons?.[g.id];
                              const active = g.selectionType==='single' ? selVal===opt.id : (selVal||[]).includes(opt.id);
                              return (
                                <div key={opt.id} onClick={()=>toggleAddon(slot.id,g.id,opt.id,g.selectionType)} style={{
                                  padding: '4px 9px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                                  border: `1px solid ${active ? '#D85A30' : '#eee'}`,
                                  background: active ? '#FAECE7' : '#fff', color: active ? '#993C1D' : '#555',
                                }}>{optionLabel(opt)} {pr>0?`+₹${pr}`:'Free'}</div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      paddingTop: 12, borderTop: '1px solid #eee' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Total: <span style={{ color: '#D85A30' }}>₹{combo.price + extraTotal}</span></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAdd} disabled={!allFilled}>Add to Bill</button>
          </div>
        </div>
        {!allFilled && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 8 }}>Select an item (and size, for pizzas) in every slot to continue</div>}
      </div>
    </div>
  );
}